import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { format } from "date-fns";
import {
  FiArrowLeft,
  FiPackage,
  FiUser,
  FiMapPin,
  FiCalendar,
  FiDollarSign,
  FiShoppingBag,
} from "react-icons/fi";
import { getData } from "../../utils/api";
import { formatAmountLegacy } from "../../utils/formatters";

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: orderData,
    isLoading,
    error,
  } = useQuery(
    ["orderDetail", orderId],
    () => getData(`/data/order/aggregated-order-details/${orderId}`),
    {
      enabled: !!orderId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );


  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-800 border-green-200";
      case "returned":
        return "bg-red-100 text-red-800 border-red-200";
      case "cancelled":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "processing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "✅";
      case "returned":
        return "↩️";
      case "cancelled":
        return "❌";
      case "processing":
        return "⚙️";
      case "pending":
        return "⏳";
      default:
        return "📦";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">
            Error Loading Order
          </h2>
          <p className="text-gray-600 mb-4">
            {error.message || "Failed to load order details"}
          </p>
          <button
            onClick={() => navigate("/orders")}
            className="btn btn-primary"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!orderData?.data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">
            Order Not Found
          </h2>
          <button
            onClick={() => navigate("/orders")}
            className="btn btn-primary"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const order = orderData.data;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              // Check if we came from a specific page via navigation state
              if (location.state?.fromPage) {
                // Navigate back to the specific page with preserved filters
                navigate(location.state.fromPage, {
                  state: {
                    startDate: location.state.startDate,
                    endDate: location.state.endDate,
                    postingAccount: location.state.postingAccount,
                    zone: location.state.zone,
                    status: location.state.status,
                    fetchType: location.state.fetchType,
                    searchValue: location.state.searchValue,
                    sortField: location.state.sortField,
                    sortValue: location.state.sortValue,
                    page: location.state.page,
                  },
                });
              } else {
                // Default fallback - go back in history
                navigate(-1);
              }
            }}
            className="btn btn-outline btn-sm"
          >
            <FiArrowLeft className="mr-2" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Order Details</h1>
            <p className="text-gray-600">Order ID: {order.refNo}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Order Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Summary Card */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <FiPackage className="text-blue-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-800">
                Order Summary
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-medium">{order.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plat. Order ID:</span>
                  <span className="font-medium">{order.refNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform:</span>
                  <span className="font-medium capitalize">
                    {order.postingAccount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Zone:</span>
                  <span className="font-medium capitalize">{order.zone}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Entry Date:</span>
                  <span className="font-medium">
                    {formatDate(order.entryDate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plat. Order Date:</span>
                  <span className="font-medium">
                    {formatDate(order.refDate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Items:</span>
                  <span className="font-medium">{order.totalItems}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Items List */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <FiShoppingBag className="text-purple-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-800">
                Order Items
              </h2>
            </div>

            <div className="space-y-4">
              {order.items?.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800 mb-1">
                        {item.productName}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-gray-600">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        item.status
                      )}`}
                    >
                      {getStatusIcon(item.status)} {item.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Quantity:</span>
                      <p className="font-medium">{item.qty}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <p className="font-medium">{formatAmountLegacy(item.amount)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Tax:</span>
                      <p className="font-medium">
                        {formatAmountLegacy(item.taxAmount)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Batch:</span>
                      <p className="font-medium">{item.batch || "N/A"}</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                    <div className="flex justify-between">
                      <span>Created: {formatDate(item.createdAt)}</span>
                      <span>Updated: {formatDate(item.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Financial Summary */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <FiDollarSign className="text-green-600 text-xl" />
              <h2 className="text-lg font-semibold text-gray-800">
                Payment Summary
              </h2>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-medium">
                  {formatAmountLegacy(order.totalAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax Amount:</span>
                <span className="font-medium">
                  {formatAmountLegacy(order.totalTaxAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Net Amount:</span>
                <span className="font-medium text-green-600">
                  {formatAmountLegacy(order.totalNetAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Customer Information Card */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <FiUser className="text-green-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-800">
                Customer Information
              </h2>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <FiUser className="text-gray-400 mt-1" />
                <div>
                  <span className="text-gray-600">Customer Name:</span>
                  <p className="font-medium">{order.customerName || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiMapPin className="text-gray-400 mt-1" />
                <div>
                  <span className="text-gray-600">Address:</span>
                  <p className="font-medium">{order.address || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
